/**
 * Debug Logger for Root Cause Investigation
 *
 * Comprehensive logging system to trace:
 * 1. White Flash Issue - Navigation and background rendering
 * 2. Button Click Issue - State transitions and event handling
 *
 * Usage:
 * import { debugLog } from '../lib/debug-logger';
 * debugLog('navigation', 'Screen mounted', { screen: 'analyze', id: params.id });
 */

type LogCategory =
  | 'navigation'      // Screen transitions, route changes
  | 'background'      // Background color rendering
  | 'animation'       // Animation lifecycle
  | 'button'          // Button state and events
  | 'state'           // Component state changes
  | 'mount'           // Component mount/unmount
  | 'event'           // User interactions
  | 'query'           // React Query cache operations
  ;

interface LogEntry {
  timestamp: string;
  category: LogCategory;
  message: string;
  data?: Record<string, unknown>;
  stack?: string;
}

class DebugLogger {
  private logs: LogEntry[] = [];
  private enabled = __DEV__; // Only enabled in development
  private maxLogs = 500; // Keep last 500 logs

  log(category: LogCategory, message: string, data?: Record<string, unknown>) {
    if (!this.enabled) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      category,
      message,
      data,
      stack: new Error().stack?.split('\n')[3], // Caller location
    };

    this.logs.push(entry);

    // Keep only last maxLogs
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    // Console log with category prefix
    const prefix = `[${category.toUpperCase()}]`;
    const dataStr = data ? JSON.stringify(data, null, 2) : '';
    console.log(`${prefix} ${message}`, dataStr);
  }

  // Get all logs for a category
  getCategory(category: LogCategory): LogEntry[] {
    return this.logs.filter(log => log.category === category);
  }

  // Get logs in time range
  getTimeRange(startTime: string, endTime: string): LogEntry[] {
    return this.logs.filter(
      log => log.timestamp >= startTime && log.timestamp <= endTime
    );
  }

  // Get all logs
  getAll(): LogEntry[] {
    return [...this.logs];
  }

  // Export logs for analysis
  export(): string {
    return JSON.stringify(this.logs, null, 2);
  }

  // Clear logs
  clear() {
    this.logs = [];
  }

  // Critical path logger - only logs in development
  critical(category: LogCategory, message: string, data?: Record<string, unknown>) {
    if (!__DEV__) return;

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      category,
      message,
      data,
      stack: new Error().stack?.split('\n')[3],
    };

    this.logs.push(entry);
    console.error(`[CRITICAL-${category.toUpperCase()}]`, message, data);
  }
}

// Singleton instance
const logger = new DebugLogger();

// Export helper functions
export function debugLog(category: LogCategory, message: string, data?: Record<string, unknown>) {
  logger.log(category, message, data);
}

export function criticalLog(category: LogCategory, message: string, data?: Record<string, unknown>) {
  logger.critical(category, message, data);
}

export function getLogs() {
  return logger.getAll();
}

export function exportLogs() {
  return logger.export();
}

export function clearLogs() {
  logger.clear();
}

// Make logger available in console for manual inspection (development only)
if (__DEV__ && typeof window !== 'undefined') {
  (window as any).__debugLogger = logger;
  console.log('🔍 Debug logger initialized. Access via window.__debugLogger');
  console.log('   Example: window.__debugLogger.getCategory("navigation")');
  console.log('   Example: window.__debugLogger.export()');
}
